import React, {
  forwardRef,
  useId,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Z_INDEX, zIndexStyle } from "@/utils/z-index";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options?: SelectOption[];
  /** Children can be used instead of options prop (Material UI compatibility) */
  children?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
}

/**
 * Arc-style Select component
 *
 * Features:
 * - Custom dropdown with smooth animations
 * - Multiple sizes: sm, md, lg
 * - Label and helper text support
 * - Error state
 * - Keyboard navigation
 * - Click outside to close
 */
const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options: optionsProp = [],
      children,
      value,
      defaultValue,
      onChange,
      placeholder = "Select an option",
      label,
      error,
      helperText,
      size = "md",
      fullWidth = true,
      disabled = false,
      required = false,
      className = "",
      id,
    },
    ref,
  ) => {
    const generatedId = useId();
    const selectId = id || generatedId;
    const hasError = !!error;

    // Extract options from children if provided (Material UI compatibility)
    const options: SelectOption[] = React.useMemo(() => {
      if (optionsProp.length > 0) return optionsProp;
      if (!children) return [];

      const extractedOptions: SelectOption[] = [];
      React.Children.forEach(children, (child) => {
        if (
          React.isValidElement(child) &&
          (child.props as any).value !== undefined
        ) {
          const props = child.props as any;
          extractedOptions.push({
            value: String(props.value),
            label:
              typeof props.children === "string"
                ? props.children
                : String(props.children),
            disabled: props.disabled,
          });
        }
      });
      return extractedOptions;
    }, [optionsProp, children]);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(
      value || defaultValue || "",
    );
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState<{
      top: number;
      left: number;
      width: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // Update dropdown position when open
    const updatePosition = useCallback(() => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }, []);

    useEffect(() => {
      if (!isOpen) return;
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }, [isOpen, updatePosition]);

    // Sync with controlled value
    useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value);
      }
    }, [value]);

    // Close on click outside (check both trigger and portal dropdown)
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const inTrigger = containerRef.current?.contains(target);
        const inDropdown = (target as HTMLElement).closest?.(
          `[data-select-id="${selectId}"]`,
        );
        if (!inTrigger && !inDropdown) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [selectId]);

    // Keyboard navigation
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev < options.length - 1 ? prev + 1 : 0,
            );
            break;
          case "ArrowUp":
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : options.length - 1,
            );
            break;
          case "Enter":
            e.preventDefault();
            if (highlightedIndex >= 0 && !options[highlightedIndex].disabled) {
              handleSelect(options[highlightedIndex].value);
            }
            break;
          case "Escape":
            e.preventDefault();
            setIsOpen(false);
            break;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, highlightedIndex, options]);

    const handleSelect = (value: string) => {
      setSelectedValue(value);
      onChange?.(value);
      setIsOpen(false);
    };

    const selectedOption = options.find((opt) => opt.value === selectedValue);

    // Size styles
    const sizeStyles = {
      sm: {
        trigger: "py-1.5 px-3 text-sm",
        label: "text-xs mb-1.5",
        option: "py-1.5 px-3 text-sm",
      },
      md: {
        trigger: "py-2.5 px-4 text-sm",
        label: "text-sm mb-2",
        option: "py-2 px-4 text-sm",
      },
      lg: {
        trigger: "py-3.5 px-5 text-base",
        label: "text-sm mb-2",
        option: "py-3 px-5 text-base",
      },
    };

    const currentSize = sizeStyles[size];

    return (
      <div
        ref={ref}
        className={`relative ${fullWidth ? "w-full" : ""} ${className}`}
      >
        {label && (
          <label
            htmlFor={selectId}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div ref={containerRef} className="relative">
          {/* Trigger button */}
          <button
            id={selectId}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={`
              w-full
              flex items-center justify-between
              bg-surface-elevated
              border rounded-arc-md
              font-medium
              text-left
              transition-all duration-arc-normal ease-arc
              outline-none
              ${currentSize.trigger}
              ${
                hasError
                  ? "border-transparent ring-2 ring-error-main"
                  : isOpen
                    ? "border-transparent ring-2 ring-accent"
                    : "border-arc-border hover:border-arc-border-strong focus:border-transparent focus:ring-2 focus:ring-accent"
              }
              ${disabled ? "bg-surface-tertiary text-content-tertiary cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            <span
              className={
                selectedOption
                  ? "text-content-primary"
                  : "text-content-tertiary"
              }
            >
              {selectedOption?.label || placeholder}
            </span>
            <svg
              className={`w-4 h-4 text-content-tertiary transition-transform duration-arc-fast ${isOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Dropdown rendered via portal to escape overflow-hidden containers */}
        {createPortal(
          <AnimatePresence>
            {isOpen && dropdownPos && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                data-select-id={selectId}
                style={{
                  position: "fixed",
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  width: dropdownPos.width,
                  ...zIndexStyle(Z_INDEX.POPOVER_IN_MODAL),
                }}
                className="bg-surface-elevated border border-arc-border rounded-arc-md shadow-arc-lg overflow-hidden"
              >
                <ul
                  ref={listRef}
                  className="max-h-60 overflow-y-auto py-1"
                  role="listbox"
                >
                  {options.map((option, index) => (
                    <li
                      key={option.value}
                      role="option"
                      aria-selected={option.value === selectedValue}
                      onClick={() =>
                        !option.disabled && handleSelect(option.value)
                      }
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`
                        ${currentSize.option}
                        cursor-pointer
                        transition-colors duration-arc-fast
                        ${
                          option.disabled
                            ? "text-content-tertiary cursor-not-allowed"
                            : option.value === selectedValue
                              ? "bg-accent-pressed text-accent font-medium"
                              : highlightedIndex === index
                                ? "bg-accent-hover"
                                : "hover:bg-accent-hover"
                        }
                      `}
                    >
                      {option.label}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

/**
 * Option component for Select - used for compatibility with Material Tailwind API
 * This allows using Select with children syntax: <Select><Option value="1">One</Option></Select>
 */
export interface OptionProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export const Option: React.FC<OptionProps> = ({
  value,
  children,
  disabled,
}) => {
  // This component is a placeholder for the declarative API
  // The actual rendering is handled by the Select component
  return null;
};

Option.displayName = "Option";

export default Select;
